import { PercentileData } from "../services/types";
import { precisionMapping } from "./dashboardUtils";

export const generatePageNumbers = (totalPages: number, currentPage: number): (number | string)[] => {
    let pageNumbers: (number | string)[] = [];

    // Case 1: Less than 5 pages
    if (totalPages <= 5) {
        for (let i = 1; i <= totalPages; i++) {
            pageNumbers.push(i);
        }
    } else {
        // Case 2: More than 5 pages
        let startPage = Math.max(1, currentPage - 1);
        let endPage = Math.min(startPage + 2, totalPages);

        // Add first page and ellipsis if needed
        pageNumbers.push(1);
        if (startPage > 2) {
            pageNumbers.push('...');
        }

        // Add intermediate pages
        for (let i = startPage + 1; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        // Add last page and ellipsis if needed
        if (endPage < totalPages - 1) {
            pageNumbers.push('...');
        }
        
        // Ensure the last page is always included
        if (endPage !== totalPages) {
            pageNumbers.push(totalPages);
        }
    }

    return pageNumbers;
};

// number formatting for visible numbers
export function addCommas(val: number | string) {
    if (val != undefined) {
        val = val.toString().replace(/,/g, ''); 
        var valSplit = val.split('.'); 

        while (/(\d+)(\d{3})/.test(valSplit[0].toString())) {
            valSplit[0] = valSplit[0].toString().replace(/(\d+)(\d{3})/, '$1' + ',' + '$2');
        }

        if (valSplit.length == 2) { 
            val = valSplit[0] + "." + valSplit[1];
        } else {
            val = valSplit[0];
        }
    }

    return val;
}

// calculateAvailableYears - get current year and historial years for ADO data
export const calculateAvailableYears = (): string[] => {
    const currentYear = new Date().getFullYear();
    const futureYear = currentYear + 1;
    const yearsArray = Array.from({ length: futureYear - 2022 + 1 }, (_, index) => (2022 + index).toString());
    return yearsArray;
};

export const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    let month = (today.getMonth() + 1).toString().padStart(2, '0');
    let day = today.getDate().toString().padStart(2, '0');

    // Format the date as YYYYMMDD
    const formattedDate = `${year}${month}${day}`;

    return formattedDate;
}

export function calculateFiscalYear(selectedFY: number | string) {
    // If there's a selected fiscal year in the session state, return it
    if (selectedFY) {
        return selectedFY;
    }

    // Get the current month and year
    const currentMonth = new Date().getMonth() + 1; 
    const currentYear = new Date().getFullYear();

    // Determine the fiscal year
    let fiscalYear;
    if (currentMonth >= 9) {
        fiscalYear = currentYear + 1;
    } else {
        fiscalYear = currentYear;
    }

    return fiscalYear;
}

export function findFiscalYear(dateString: string) {
    // Parse the date string into a Date object
    const dateParts = dateString.split('/');
    const month = parseInt(dateParts[0], 10);
    const year = parseInt(dateParts[2], 10);

    

    // Determine the fiscal year based on the month
    let fiscalYear;
    if (month >= 9) {
        fiscalYear = year + 1;
    } else {
        fiscalYear = year;
    }
    return fiscalYear;
}

export const formatValue = (value: number | string, unit: string, metricId?: number): string => {
    if (value === null) return 'N/A';
    
    // Get precision override if available, default to 0 for now
    const precision = metricId && precisionMapping[metricId] !== undefined 
        ? precisionMapping[metricId] 
        : 0;

    switch (unit) {
        case "integer":
            if (Number(value) >= 1000000) {
                return `${(Number(value) / 1000000).toFixed(2)}m`;
            } else if (Number(value) >= 100000) {
                return `${Math.floor(Number(value) / 1_000)}k`;
            } else return addCommas(Number(value).toFixed(0));
        case "percentage":
            return `${(Number(value) * 100).toFixed(precision)}%`;
        case "currency":
            if (Number(value) >= 1000000) {
                return `$${(Number(value) / 1000000).toFixed(2)}m`;
            } else if (Number(value) >= 100000) {
                return `$${Math.floor(Number(value) / 1_000)}k`;
            } else return `$${addCommas(Number(value).toFixed(0))}`;
        case "ratio":        
            // Assuming value is a string in the format n/d
            const [numeratorStr, denominatorStr] = (value as string).split('/');
            const numerator = Number(numeratorStr);
            const denominator = Number(denominatorStr);

            // // Set a tolerance value for ratio comparison
            const tolerance = 0.05; // Adjust tolerance as needed

            // Use getRatio function
            const ratioResult = getRatio(numerator, denominator, tolerance);
            return ratioResult !== null ? ratioResult : 'N/A';

        case "decimal":
            if (Number(value) >= 1000000) {
                return `${(Number(value) / 1000000).toFixed(2)}m`;
            } else if (Number(value) >= 100000) {
                return `${Math.floor(Number(value) / 1_000)}k`;
            } else return addCommas(Number(value).toFixed(precision));
        default:
            return value.toString();
    }
};

export const formatValueBase = (value: number | string, unit: string): string => {
    if (value === null) return 'N/A';

    switch (unit) {
        case "integer":
            return addCommas(Number(value).toFixed(0));
        case "percentage":
            return `${(Number(value) * 100).toFixed(0)}%`;
        case "currency":
            return `$${addCommas(Number(value).toFixed(0))}`;
        case "ratio":
        // Assuming value is a string in the format n/d
        const [numeratorStr, denominatorStr] = (value as string).split('/');
        const numerator = Number(numeratorStr);
        const denominator = Number(denominatorStr);

        // Set a tolerance value for ratio comparison
        const tolerance = 0.05; // Adjust tolerance as needed

        // Use getRatio function
        const ratioResult = getRatio(numerator, denominator, tolerance);
        return ratioResult !== null ? ratioResult : 'N/A';

        case "decimal":
            return addCommas(Number(value).toFixed(2));
        default:
            return value.toString();
    }
};

export function getRatio(a: number, b: number, tolerance: number): string {
    let bg = Math.max(a, b);
    let sm = Math.min(a, b);

    for (let i = 1; i < 1000000; i++) {
        let d = sm / i;
        let res = bg / d;
        let roundedRes = parseFloat(res.toFixed(0)); 
        let howClose = Math.abs(res - roundedRes);

        if (howClose < tolerance) {
            return a > b ? `${roundedRes}:${i}` : `${i}:${roundedRes}`;
        }
    }

    return 'N/A';
}

export function capitalizeFirstLetter(word: string) {
    if (typeof word !== 'string' || word.length === 0) {
      return word;
    }
    return word.charAt(0).toUpperCase() + word.slice(1);
}

// Function to capitalize the first letter of each word
export const capitalizeWords = (phrase: string) => {
    return phrase
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
};

export const formatDate = (dateString: string): string => {
    if (!dateString) return '';

    const date = new Date(dateString); // Parse the string into a Date object
    const month = date.getMonth() + 1; // getMonth() is 0-indexed, so add 1
    const day = date.getDate();
    const year = date.getFullYear();

    // Format the date as mm/dd/yyyy
    return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
};

// Categorize function with stars
export const categorizeIndexValue = (val: number, percentile: PercentileData[], showStars: boolean): string  => {

    // Ensure bins data is available
    if (!percentile || percentile.length === 0) {
        return "No percentile data available";
    }

    const version = percentile[0].versions[0];
    
    // Define the bin ranges based on the new percentile values
    const bins = [
        { min: parseFloat(version.pct0), max: parseFloat(version.pct20), category: "Very Low", stars: "⭐" },
        { min: parseFloat(version.pct20), max: parseFloat(version.pct40), category: "Low", stars: "⭐⭐" },
        { min: parseFloat(version.pct40), max: parseFloat(version.pct60), category: "Moderate", stars: "⭐⭐⭐" },
        { min: parseFloat(version.pct60), max: parseFloat(version.pct80), category: "High", stars: "⭐⭐⭐⭐" },
        { min: parseFloat(version.pct80), max: parseFloat(version.pct100), category: "Very High", stars: "⭐⭐⭐⭐⭐" },
    ];

    // Find the bin that the value falls into
    for (const bin of bins) {
        if (val >= bin.min && val <= bin.max) {
            return showStars ? `${bin.stars} ${bin.category}` : bin.category;
        }
    }

    return "Unknown";
};

export const parseMarkdown = (markdown: string) => {
    if (!markdown) return markdown;

    else return markdown
        .replace(/\n\n/g, '<br><br>') // Double newlines
        .replace(/\n/g, '<br>') // Single newlines
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold (**text**)
        .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italics (*text*)
};

export const tabColors: Record<string, string> = {
  People: "#2F5A5D",         // Dark Green Gray
  Demographics: "#2F5A5D",   // Dark Green Gray
  Economy: "#79bc77",        // Green Highlight
  Access: "#3cc6d5",         // Blue Highlight
  Accessibility: "#3cc6d5",  // Blue Highlight
  Livability: "#8d9da4",     // Gray
  Education: "#c3c26f",      // Gold Highlight
};

// EDUCATION HELPERS FOR MDD ORDERING

// Normalize small naming differences so punctuation and spacing
// do not prevent a metric from matching the custom order.
const normalizeMetricName = (name?: string) =>
    (name ?? "")
        .toLowerCase()
        .replace(/[’']/g, "")
        .replace(/[–—]/g, "-")
        .replace(/\s*\+\s*/g, "+")
        .replace(/\s+/g, " ")
        .trim();

const educationMetricOrderGroups: string[][] = [
    [
        "Age 3 and 4 School Enrollment",
    ],
    [
        "Nursery or Preschool Enrollment",
    ],
    [
        "Kindergarten Enrollment",
    ],
    [
        "Elementary School Enrollment (Grades 1–4)",
        "Elementary School Enrollment (Grades 1-4)",
    ],
    [
        "Elementary School Enrollment (Grades 5–8)",
        "Elementary School Enrollment (Grades 5-8)",
    ],
    [
        "High School Enrollment (Grades 9–12)",
        "High School Enrollment (Grades 9-12)",
    ],
    [
        "Undergraduate College Enrollment",
    ],
    [
        "Graduate or Professional School Enrollment",
    ],
    [
        "4-Year High School Graduation Rate",
    ],
    [
        "Age 25+ without HS Diploma/GED",
        "Age 25+ without a HS Diploma/GED",
    ],
    [
        "Age 25+ with HS Diploma/GED",
        "Age 25+ with a HS Diploma/GED",
    ],
    [
        "Age 25+ with a Bachelor's Degree+",
        "Age 25+ with Bachelor's Degree+",
    ],
    [
        "Master's Degree+",
        "Masters Degree+",
    ],
];

const educationMetricOrder = new Map<string, number>();

educationMetricOrderGroups.forEach((names, index) => {
    names.forEach((name) => {
        educationMetricOrder.set(
            normalizeMetricName(name),
            index
        );
    });
});

export const getEducationMetricOrder = (name?: string) =>
    educationMetricOrder.get(
        normalizeMetricName(name)
    ) ?? Number.MAX_SAFE_INTEGER;


const guessFileType = (url: any) => {
  try {
    const u = new URL(url);
    const ext: any = u.pathname.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'PDF';
    if (['xls','xlsx','csv'].includes(ext)) return 'Spreadsheet';
    if (['doc','docx'].includes(ext)) return 'Document';
    return null;
  } catch { return null; }
};

const prettifyPath = (pathname: any) => {
  if (!pathname || pathname === '/') return '';
  // Take last non-empty segment, replace hyphens with spaces, title case
  const seg = pathname.split('/').filter(Boolean).pop() || '';
  const clean = seg.replace(/[-_]+/g, ' ');
  return clean.charAt(0).toUpperCase() + clean.slice(1);
};

export const buildLinkLabel = (rawUrl: any, options: any = {}) => {
  const { fallback = 'Source' } = options;
  try {
    const u = new URL(rawUrl);
    const host = u.hostname.replace(/^www\./, '');
    const pathHint = prettifyPath(u.pathname);
    const fileType = guessFileType(rawUrl);

    // e.g., "CDC — Vaccination coverage report (PDF)"
    const main = pathHint ? `${host} — ${pathHint}` : host;
    return fileType ? `${main} (${fileType})` : main;
  } catch {
    return fallback;
  }
};

/**
 * Safely parses a comma-separated string of URLs.
 * Handles cases where commas exist inside query params (e.g. Census `g=` param).
 */
export const parseCommaSeparatedUrls = (sourceUrlString?: string): string[] => {
  if (!sourceUrlString) return [];

  return sourceUrlString
    // Split only on commas that are followed by a new URL
    .split(/,\s*(?=https?:\/\/)/i)
    .map((url) => url.trim())
    .filter(Boolean);
};